import type { Organization, OrganizationWithDevices, PaginatedResponse, User } from '@eppendorf/vnls-user-tenant-utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError, AxiosResponse } from 'axios';

import { fetcher } from '$shared/fetch';
import { createPathWithQueryParams, MIGRATION_BASE_PATH, ORGANIZATIONS_YGGDRASIL_BASE_PATH, objectToURLSearchParams } from '$shared/utils/api-paths';

export const organizationQueryKey = 'organizations';

export const useOrganizationsWithQueryParams = (queryParams = {}) => {
  const queryKey = [organizationQueryKey, ...objectToURLSearchParams(queryParams).values()];

  return useQuery({
    queryKey,
    queryFn: async () =>
      fetcher.get<PaginatedResponse<OrganizationWithDevices>>(createPathWithQueryParams(ORGANIZATIONS_YGGDRASIL_BASE_PATH, queryParams)),
    placeholderData: (previousData) => previousData,
  });
};

export const useGetOrganization = (id: string) => {
  const queryKey = [organizationQueryKey, id];

  return useQuery({
    queryKey,
    queryFn: async () => fetcher.get<Organization>(`${ORGANIZATIONS_YGGDRASIL_BASE_PATH}/${id}`),
    enabled: !!id,
  });
};

export const useGetMigrationReport = (legacyTenantId: string) => {
  const queryKey = [organizationQueryKey, 'migration-report', legacyTenantId];

  return useQuery({
    queryKey,
    queryFn: async () => {
      try {
        const { content } = await fetcher.get<{ content: string }>(`${MIGRATION_BASE_PATH}/migration/reportContent/${legacyTenantId}`);
        return content;
      } catch (error) {
        // eslint-disable-next-line no-console -- for debugging
        console.log(error);
        return null;
      }
    },
    enabled: !!legacyTenantId,
  });
};

export const useAddOrganization = () => {
  const queryClient = useQueryClient();
  const addOrganization = async (organization: Partial<Organization>) =>
    fetcher.post<Partial<Organization>, Organization>(ORGANIZATIONS_YGGDRASIL_BASE_PATH, organization);

  return useMutation<AxiosResponse<Organization>, AxiosError, Partial<Organization>>({
    mutationFn: addOrganization,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [organizationQueryKey] });
    },
  });
};

export const useUpdateOrganization = () => {
  const queryClient = useQueryClient();
  const updateOrganization = (organization: Partial<Organization>) => {
    const { id, ...organizationWithoutId } = organization;
    return fetcher.patch<Partial<Organization>, Organization>(`${ORGANIZATIONS_YGGDRASIL_BASE_PATH}/${id}`, organizationWithoutId);
  };

  return useMutation<Organization, AxiosError, Partial<Organization>>({
    mutationFn: updateOrganization,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [organizationQueryKey] });

      queryClient.setQueryData([organizationQueryKey, data.id], data);
    },
  });
};

export const useDeleteOrganization = () => {
  const queryClient = useQueryClient();
  const deleteOrganization = async (id: string) => fetcher.delete(`${ORGANIZATIONS_YGGDRASIL_BASE_PATH}/${id}`);
  return useMutation<AxiosResponse, AxiosError, string>({
    mutationFn: deleteOrganization,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [organizationQueryKey] });
    },
  });
};

export const useMigrateOrganizations = () => {
  const migrateOrganizations = (legacyTenantId: string) =>
    fetcher.post<unknown, { tenantId: string }>(`${MIGRATION_BASE_PATH}/migration/migrateTenant`, {
      legacyTenantId,
    });
  const queryClient = useQueryClient();
  return useMutation<AxiosResponse, AxiosError, string>({
    mutationFn: migrateOrganizations,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [organizationQueryKey] });
    },
  });
};

export const useMigrateEvents = () => {
  const migrateEvents = (legacyTenantId: string) =>
    fetcher.post<unknown, { tenantId: string }>(`${MIGRATION_BASE_PATH}/migration/migrateEvents`, {
      legacyTenantId,
    });
  const queryClient = useQueryClient();
  return useMutation<AxiosResponse, AxiosError, string>({
    mutationFn: migrateEvents,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [organizationQueryKey] });
    },
  });
};

export const useMigrateTelemetry = () => {
  const migrateEvents = (legacyTenantId: string) =>
    fetcher.post<unknown, { tenantId: string }>(`${MIGRATION_BASE_PATH}/migration/migrateTelemetry`, {
      legacyTenantId,
    });
  const queryClient = useQueryClient();
  return useMutation<AxiosResponse, AxiosError, string>({
    mutationFn: migrateEvents,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [organizationQueryKey] });
    },
  });
};

export const useResendInvite = () => {
  const queryClient = useQueryClient();
  const updateInviteSendAt = async (id: string) =>
    fetcher.post<unknown, Organization>(`${ORGANIZATIONS_YGGDRASIL_BASE_PATH}/${id}/actions/resend-invite`, undefined);

  return useMutation<AxiosResponse<Organization>, AxiosError, string>({
    mutationFn: updateInviteSendAt,
    onSuccess: ({ data }) => {
      queryClient.invalidateQueries({ queryKey: [organizationQueryKey] });

      queryClient.setQueryData([organizationQueryKey, data.id], data);
    },
    onError: (error) => {
      // eslint-disable-next-line no-console -- for debugging
      console.log(error);
    },
  });
};

export const useInviteUsers = () => {
  const inviteAllUsers = (legacyTenantId: string) =>
    fetcher.post<unknown, { tenantId: string }>(`${MIGRATION_BASE_PATH}/migration/invite-all-users`, {
      legacyTenantId,
    });

  const queryClient = useQueryClient();
  return useMutation<AxiosResponse<{ tenantId: string }>, AxiosError, string>({
    mutationFn: inviteAllUsers,
    onSuccess: ({ data }: AxiosResponse<{ tenantId: string }>) => {
      queryClient.invalidateQueries({ queryKey: [organizationQueryKey] });

      queryClient.setQueryData([organizationQueryKey, data.tenantId], data);
    },
    onError: (error: AxiosError) => {
      // eslint-disable-next-line no-console -- for debugging
      console.log(error);
    },
  });
};

export const downloadMigrationReport = async (legacyTenantId: string) => {
  try {
    return await fetcher.get<{ url: string }>(`${MIGRATION_BASE_PATH}/migration/report/${legacyTenantId}`);
  } catch (_error) {
    return { url: null };
  }
};

export const enableSupportAccount = (organizationId: string) =>
  useMutation<AxiosResponse, AxiosError>({
    mutationFn: () => fetcher.post(`${ORGANIZATIONS_YGGDRASIL_BASE_PATH}/${organizationId}/support/user`, null),
  });

export const disableSupportAccount = (organizationId: string) =>
  useMutation<AxiosResponse, AxiosError>({
    mutationFn: () => fetcher.delete(`${ORGANIZATIONS_YGGDRASIL_BASE_PATH}/${organizationId}/support/user`),
  });

export const getSupportAccount = async (organizationId: string) => {
  try {
    return await fetcher.get<User>(`${ORGANIZATIONS_YGGDRASIL_BASE_PATH}/${organizationId}/support/user`);
  } catch (_error) {
    return null;
  }
};

export const useGetReports = (filters: string[]) =>
  useMutation<AxiosResponse, AxiosError>({
    mutationFn: () => fetcher.post(`${ORGANIZATIONS_YGGDRASIL_BASE_PATH}/reports`, { filters }),
  });

export const getMigrationStatus = (organizationId: string) => {
  const queryKey = [organizationQueryKey, 'migration-status'];

  return useQuery({
    queryKey,
    queryFn: () =>
      fetcher.get<{ isMigratingEvents: boolean; isMigratingTelemetry: boolean; isMigratingLongTerm: boolean }>(
        `${MIGRATION_BASE_PATH}/migration/status/${organizationId}`,
      ),
    enabled: !!organizationId,
    staleTime: 1_000,
    gcTime: 0,
  });
};

export const useMigrateLongTermBackup = () => {
  const queryClient = useQueryClient();
  return useMutation<AxiosResponse, AxiosError, { legacyTenantId: string; tenantId: string }>({
    mutationFn: ({ legacyTenantId, tenantId }) =>
      fetcher.post(`${MIGRATION_BASE_PATH}/migration/migrateLongTerm`, {
        legacyTenantId,
        tenantId,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [organizationQueryKey] }),
  });
};

export const getDeviceLogs = (organizationId: string, fileName: string) =>
  fetcher.get<{ presignedUrl: string }>(`${ORGANIZATIONS_YGGDRASIL_BASE_PATH}/${organizationId}/download-device-logs/${fileName}`);

export const useGetAvailableDeviceLogs = (organizationId: string) => {
  const queryKey = [organizationQueryKey, 'available-device-logs'];

  return useQuery({
    queryKey,
    queryFn: () =>
      fetcher.get<{ files: { fileName: string; creationDate: string }[] }>(
        `${ORGANIZATIONS_YGGDRASIL_BASE_PATH}/${organizationId}/get-available-devices-logs`,
      ),
    enabled: !!organizationId,
    staleTime: 1_000,
    gcTime: 0,
  });
};

export const useRequestLogsUpload = () =>
  useMutation<AxiosResponse, AxiosError, { tenantId: string; serialNumber: string }>({
    mutationFn: ({ tenantId, serialNumber }) =>
      fetcher.post(`${ORGANIZATIONS_YGGDRASIL_BASE_PATH}/request-logs-upload`, {
        tenantId,
        serialNumber,
      }),
  });
